import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { socials } from "../../data/links";
import { Buttons } from "../Buttons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../utilis/api";

gsap.registerPlugin(ScrollTrigger);

function ContactForm() {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const inputRefs = useRef([]);
  const textareaRef = useRef(null);
  const buttonRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  // Handle input changes
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      };

      const res = await api.post("/contact", payload);

      if (res.data.success) {
        toast.success(res.data.message || "Query submitted successfully!");

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Something went wrong. Try again!",
      );
    }
  };

  // GSAP Animations
  useEffect(() => {
    gsap.from(leftRef.current, {
      x: -100,
      opacity: 0,
      duration: 1.2,
      ease: "power3.out",
      scrollTrigger: {
        trigger: leftRef.current,
        start: "top 80%",
        toggleActions: "play none none reverse",
      },
    });

    gsap.from(rightRef.current, {
      x: 100,
      opacity: 0,
      duration: 1.2,
      ease: "power3.out",
      scrollTrigger: {
        trigger: rightRef.current,
        start: "top 80%",
        toggleActions: "play none none reverse",
      },
    });

    inputRefs.current.forEach((input, i) => {
      gsap.from(input, {
        y: 30,
        opacity: 0,
        duration: 0.8,
        delay: i * 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: input,
          start: "top 90%",
          toggleActions: "play none none reverse",
        },
      });
    });

    gsap.from(textareaRef.current, {
      y: 30,
      opacity: 0,
      duration: 0.8,
      ease: "power3.out",
      scrollTrigger: {
        trigger: textareaRef.current,
        start: "top 90%",
        toggleActions: "play none none reverse",
      },
    });

    gsap.from(buttonRef.current, {
      scale: 0.8,
      opacity: 0,
      duration: 1,
      ease: "back.out(1.7)",
      scrollTrigger: {
        trigger: buttonRef.current,
        start: "top 90%",
        toggleActions: "play none none reverse",
      },
    });
  }, []);

  return (
    <div className="bg-black text-white flex items-center justify-center overflow-x-hidden">
      <ToastContainer />
      <div className="max-w-7xl px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left Section */}
          <div ref={leftRef} className="space-y-2">
            <h1
              className="font-syne font-bold leading-none
              text-[50px] text-center md:text-start sm:text-[100px] md:text-[130px] lg:text-[160px] xl:text-[184px]
              text-gradient-white"
            >
              Let’s Talk
            </h1>
            <div className="flex justify-center md:justify-start items-center gap-[1.5rem] p-2">
              {socials.map((data, index) => (
                <img
                  key={index}
                  style={{ width: "30px" }}
                  onClick={() => window.open(data.link, "_blank")}
                  src={data.icon}
                  className="cursor-pointer"
                />
              ))}
            </div>
          </div>

          {/* Right Section - Form */}
          <div
            ref={rightRef}
            className="bg-gradient-to-br from-[#1f1f1f] to-[#2b2b2b] backdrop-blur-md p-8 rounded-2xl shadow-lg"
          >
            <form className="space-y-6 font-outfit" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { label: "First Name", name: "firstName" },
                  { label: "Last Name", name: "lastName" },
                  { label: "Email Address", name: "email" },
                  { label: "Phone Number", name: "phone" },
                ].map((field, idx) => (
                  <div key={idx} ref={(el) => (inputRefs.current[idx] = el)}>
                    <label className="block mb-1 text-sm font-semibold">
                      {field.label}
                    </label>
                    <input
                      type={
                        field.name === "email"
                          ? "email"
                          : field.name === "phone"
                            ? "tel"
                            : "text"
                      }
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={`Enter Your ${field.label} ...`}
                      className="w-full p-3 rounded-lg border border-gray-600 bg-transparent text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                ))}
              </div>

              {/* Message */}
              <div ref={textareaRef}>
                <label className="block mb-1 text-sm font-semibold">
                  Message
                </label>
                <textarea
                  name="message"
                  rows="5"
                  placeholder="Enter Your Message ..."
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full p-3 rounded-lg border border-gray-600 bg-transparent text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                ></textarea>
              </div>

              <p className="text-sm text-gray-400">
                We respect your privacy — your email address is safe with us. No
                spam, no nonsense — just great scents.
              </p>

              <div ref={buttonRef}>
                <Buttons title={"Submit"} type="submit" />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactForm;
